import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Lock, AlertTriangle, PenLine } from "lucide-react";

/* Public clearance portal (special tokened link).
 * HR + the offboarding employee monitor progress here; each department head's
 * emailed link opens the same page with their DocuSign-style signing panel.
 * Rendered as a formal clearance certificate: department signature table with
 * e-signatures, dates and remarks, plus the accountabilities clause. */

const SCRIPT_FONT = { fontFamily: "'Segoe Script','Brush Script MT','Lucida Handwriting','Comic Sans MS',cursive" };

const fmtLong = (d) => {
    if (!d) return "—";
    const dt = new Date(`${d}T00:00:00`);
    return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};
const fmtShort = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso);
    return isNaN(dt) ? "" : `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};
const sigId = (s) => (s.id || "").replace(/-/g, "").slice(0, 18).toUpperCase();

export default function ClearancePortal() {
    const { token } = useParams();
    const [params] = useSearchParams();
    const signToken = params.get("signer");
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [name, setName] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const panelRef = useRef(null);

    const load = () => fetch(`/api/public/clearance/${token}${signToken ? `?signer=${signToken}` : ""}`)
        .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "Invalid link"); return r.json(); })
        .then((d) => { setData(d); if (d.me && !name) setName(d.me.signer_name); })
        .catch((e) => setErr(e.message));
    useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [token, signToken]);

    const sign = async (decision) => {
        if (decision === "flagged" && !note.trim()) return alert("Describe the outstanding accountability in the remarks.");
        setBusy(true);
        try {
            const r = await fetch(`/api/public/clearance/${token}/sign`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sign_token: signToken, decision, signed_name: name, note }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || "Signing failed");
            setData(await r.json());
        } catch (e) { alert(e.message); }
        setBusy(false);
    };

    if (err) return <Shell><div className="max-w-lg mx-auto bg-red-50 border border-red-300 text-red-800 rounded-xl p-5 text-sm flex gap-2"><AlertTriangle className="size-5 shrink-0" />{err}</div></Shell>;
    if (!data) return <Shell><div className="text-center text-slate-500">Loading clearance…</div></Shell>;

    const myId = data.me?.id;
    const canSignNow = data.me && data.me.status !== "signed" && !data.me.locked;

    // Group consecutive same-department signers so the Department cell can rowspan (Finance may have several signatories).
    const groups = [];
    for (const s of data.signers) {
        const g = groups[groups.length - 1];
        if (g && g.department === s.department) g.rows.push(s);
        else groups.push({ department: s.department, is_finance: s.is_finance, rows: [s] });
    }

    const SignatureCell = ({ s }) => {
        if (s.status === "signed" || s.status === "flagged") {
            return (
                <div className={`border-l-4 pl-2 py-0.5 ${s.status === "flagged" ? "border-red-500" : "border-indigo-600"}`}>
                    <div className="text-[9px] text-slate-500 leading-none">Signed by:</div>
                    <div className="text-xl leading-tight text-slate-900" style={SCRIPT_FONT}>{s.signed_name || s.signer_name}</div>
                    <div className="text-[8px] font-mono text-slate-400 border-t border-slate-300 mt-0.5 pt-0.5 tracking-wider">{sigId(s)}</div>
                </div>
            );
        }
        if (s.id === myId && canSignNow) {
            return (
                <button
                    onClick={() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-4 py-1.5 flex flex-col items-center leading-tight shadow"
                    data-testid="table-sign-btn"
                >
                    <span className="text-[10px] font-bold uppercase">Sign</span>
                    <PenLine className="size-4 mt-0.5" />
                </button>
            );
        }
        if (s.locked) return <div className="text-[10px] text-slate-400 flex items-center gap-1"><Lock className="size-3" /> Signs last</div>;
        return <div className="text-[10px] text-slate-400 italic">Awaiting {s.signer_name}</div>;
    };

    return (
        <Shell>
            <div className="max-w-3xl mx-auto space-y-4">
                {/* Status strip (kept above the document) */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-semibold">
                            {data.status === "cleared" ? "✅ Clearance complete" : data.status === "awaiting_finance" ? "Awaiting Finance (final signatory)" : data.status === "scheduled" ? "Scheduled — clearance starts on the last day" : "Clearance in progress"}
                        </span>
                        <span className="font-mono">{data.signed}/{data.total} signed</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full transition-all ${data.status === "cleared" ? "bg-emerald-500" : "bg-indigo-600"}`} style={{ width: `${data.progress_pct}%` }} data-testid="clearance-progress" />
                    </div>
                </div>

                {/* Signing panel for the department head who opened their emailed link */}
                {data.me && (
                    <div ref={panelRef} className="bg-indigo-50 border-2 border-indigo-400 rounded-xl p-4" data-testid="sign-panel">
                        <div className="flex items-center gap-2 font-semibold text-indigo-900"><PenLine className="size-4" /> Your signature is requested — {data.me.department}</div>
                        {data.me.status === "signed" ? (
                            <div className="text-sm text-emerald-700 mt-2">✓ You signed this clearance{data.me.signed_at ? ` on ${new Date(data.me.signed_at).toLocaleString()}` : ""}. Thank you!</div>
                        ) : data.me.locked ? (
                            <div className="text-sm text-indigo-900 mt-2"><Lock className="size-3.5 inline mr-1" /> Finance signs LAST — you'll be able to sign once every other department has cleared.</div>
                        ) : (
                            <div className="mt-2 space-y-2">
                                <div className="text-xs text-indigo-900">By signing you confirm {data.me.department} has no pending accountabilities for {data.employee_name}.</div>
                                <input className="w-full h-9 rounded-md border border-indigo-300 px-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name (electronic signature)" data-testid="sign-name" />
                                <input className="w-full h-9 rounded-md border border-indigo-300 px-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Remarks — e.g. assets received, accounts cleared (required when flagging)" data-testid="sign-note" />
                                {name.trim() && <div className="text-lg text-slate-700 px-1" style={SCRIPT_FONT}>{name}</div>}
                                <div className="flex gap-2">
                                    <button disabled={busy || !name.trim()} onClick={() => sign("signed")} className="flex-1 h-10 rounded-lg bg-indigo-600 text-white text-sm font-bold disabled:opacity-50" data-testid="sign-approve">✍ Sign — Cleared</button>
                                    <button disabled={busy} onClick={() => sign("flagged")} className="h-10 px-3 rounded-lg border border-red-400 text-red-600 text-sm font-semibold">Flag issue</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* The clearance certificate document */}
                <div className="bg-white rounded-sm border border-slate-300 shadow-md px-6 py-8 md:px-10" data-testid="clearance-certificate">
                    <div className="text-center mb-6">
                        <div className="text-xs font-bold tracking-widest text-slate-500 uppercase">Employee Clearance</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{data.employee_no} · {data.position || "Employee"} · <span className="capitalize">{data.reason}</span></div>
                    </div>

                    <p className="text-[15px] leading-relaxed text-slate-800 text-justify">
                        This is to certify that Mr./Ms. <b>{data.employee_name}</b> who has rendered their last
                        working day effective <b>{fmtLong(data.last_day)}</b>, has no outstanding financial and property obligations
                        to settle at the date of signing.
                    </p>

                    <table className="w-full mt-5 border-collapse text-sm" data-testid="clearance-table">
                        <thead>
                            <tr>
                                {["Department", "Signatory", "Date", "Remarks"].map((h) => (
                                    <th key={h} className="border border-slate-800 bg-slate-50 px-3 py-2.5 font-bold text-slate-900 text-center">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((g) => g.rows.map((s, ri) => {
                                const mine = s.id === myId && canSignNow;
                                return (
                                    <tr key={s.id} data-testid={`signer-${s.order}`}>
                                        {ri === 0 && (
                                            <td rowSpan={g.rows.length} className="border border-slate-800 px-3 py-2 font-bold text-slate-900 text-center align-middle">
                                                {g.department}
                                                {g.is_finance && <div className="text-[9px] font-bold text-indigo-700 mt-0.5">FINAL SIGNATORY</div>}
                                            </td>
                                        )}
                                        <td className="border border-slate-800 px-2 py-2 align-middle min-w-[170px]"><SignatureCell s={s} /></td>
                                        <td className="border border-slate-800 px-2 py-2 text-center align-top font-mono text-xs">{fmtShort(s.signed_at)}</td>
                                        <td className={`border border-slate-800 px-2 py-2 align-top text-xs ${mine ? "bg-indigo-50/70 ring-1 ring-inset ring-indigo-300" : ""} ${s.status === "flagged" ? "text-red-700 font-medium" : "text-slate-800"}`}>
                                            {s.status === "flagged" ? `⚑ ${s.note}` : s.status === "signed" ? (s.note || "Cleared") : ""}
                                        </td>
                                    </tr>
                                );
                            }))}
                        </tbody>
                    </table>

                    <p className="text-[13px] leading-relaxed text-slate-700 text-justify mt-5">
                        Please consider employee's accountabilities for task and document handover, debt,
                        accountable forms, loaned equipment, fixed assets, outstanding accounts, supplies,
                        deletion of all accounts/access rights and other materials.
                    </p>

                    <div className="grid grid-cols-2 gap-10 mt-12 mb-2">
                        {["Immediate Manager's Signature", "Employee's Signature"].map((label) => (
                            <div key={label}>
                                <div className="border-t border-slate-800 pt-1.5 text-[13px] text-slate-800">{label}</div>
                                <div className="text-[13px] text-slate-800">Date:</div>
                            </div>
                        ))}
                    </div>
                </div>

                {data.status === "cleared" && (
                    <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 text-center text-sm text-emerald-900" data-testid="cleared-banner">
                        <CheckCircle2 className="size-4 inline mr-1 -mt-0.5" /> Fully cleared on {data.cleared_at ? new Date(data.cleared_at).toLocaleDateString() : "—"}. HR is processing the final pay and Certificate of Employment.
                    </div>
                )}
                <div className="text-center text-[11px] text-slate-400">SAHOD HRIS · secure clearance link — do not share outside HR and the signatories</div>
            </div>
        </Shell>
    );
}

const Shell = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-100 py-10 px-4">{children}</div>
);
